import { useContext, useState } from "react"
import { Pressable, StyleSheet, Text, TextStyle, View, ViewStyle } from "react-native"

import { Image, ImageSource } from "expo-image"

import { ChannelBrandContext } from "src/context/ChannelBrandContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { ThemedStyle } from "src/theme"

const AVATAR_SIZE = 56
const WEB_BANNER_MAX_WIDTH = 360

export interface BrandBannerProps {
  /** Local require() or remote URI — overrides the channel brand image from context. */
  imageSource?: ImageSource
  /** Overrides the channel brand title from context. */
  title?: string
  /** Overrides the channel brand subtitle from context. Pass null to hide it. */
  subtitle?: string | null
  /** When provided, the banner becomes tappable and invokes this handler on press. */
  onPress?: () => void
}

/**
 * Pill-shaped brand banner displaying a channel's brand image, title, and subtitle.
 *
 * When used inside `ChannelBrandProvider` (authenticated screens) it reads data from
 * context automatically. When used outside the provider (e.g. the login screen) supply
 * `imageSource`, `title`, and `subtitle` props directly.
 */
export function BrandBanner({
  imageSource,
  title: titleProp,
  subtitle: subtitleProp,
  onPress,
}: BrandBannerProps = {}) {
  const brandCtx = useContext(ChannelBrandContext)
  const brandData = brandCtx?.brandData ?? null
  const { themed } = useAppTheme()
  const [imageError, setImageError] = useState(false)

  // Coalesce empty strings to null so we don't accidentally render the empty
  // string as a text-node child via `{value && <Element/>}` short-circuit
  // evaluation (which would trip rn-web's "Unexpected text node" warning).
  const displayTitle: string | null =
    (titleProp || brandData?.brand_title || brandData?.name) ?? null
  const displaySubtitle: string | null =
    subtitleProp !== undefined ? subtitleProp || null : brandData?.brand_subtitle || null
  const resolvedImageSource: ImageSource | null =
    imageSource ?? (brandData?.brand_image ? { uri: brandData.brand_image } : null)
  const showImage = !!resolvedImageSource && !imageError
  const hasText = !!(displayTitle || displaySubtitle)

  if (!displayTitle && !resolvedImageSource) return null

  const content = (
    <>
      {showImage ? (
        <View style={styles.avatarContainer}>
          <Image
            source={resolvedImageSource!}
            style={styles.avatarImage}
            contentFit="cover"
            accessibilityLabel={displayTitle ? `Logo for ${displayTitle}` : "Channel logo"}
            accessibilityRole="image"
            onError={() => setImageError(true)}
          />
        </View>
      ) : null}
      {hasText ? (
        <View style={[styles.textContainer, !showImage && styles.textContainerCentered]}>
          {displayTitle ? (
            <Text style={themed($titleText)} numberOfLines={1} ellipsizeMode="tail">
              {displayTitle.toUpperCase()}
            </Text>
          ) : null}
          {displaySubtitle ? (
            <Text style={themed($subtitleText)} numberOfLines={1} ellipsizeMode="tail">
              {displaySubtitle.toUpperCase()}
            </Text>
          ) : null}
        </View>
      ) : null}
    </>
  )

  if (onPress) {
    return (
      <Pressable
        style={({ pressed }) => [themed($pill), pressed && styles.pillPressed]}
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={
          displayTitle ? `${displayTitle}, go to content library` : "Go to content library"
        }
      >
        {content}
      </Pressable>
    )
  }

  return <View style={themed($pill)}>{content}</View>
}

const $pill: ThemedStyle<ViewStyle> = ({ colors, spacing }) => ({
  flexDirection: "row",
  alignItems: "center",
  backgroundColor: colors.buttonBackground,
  borderRadius: 999,
  paddingVertical: spacing.xs,
  paddingHorizontal: spacing.xs,
  marginHorizontal: spacing.md,
  marginVertical: spacing.xxs,
  minHeight: 40,
  alignSelf: "center",
  width: "100%",
  maxWidth: WEB_BANNER_MAX_WIDTH,
})

const $titleText: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  color: colors.buttonText,
  fontSize: 15,
  fontFamily: typography.primary.semiBold,
  letterSpacing: 0.8,
  lineHeight: 18,
})

const $subtitleText: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  color: colors.buttonText,
  fontSize: 11,
  fontFamily: typography.primary.normal,
  letterSpacing: 0.5,
  lineHeight: 15,
  marginTop: 1,
  opacity: 0.75,
})

const styles = StyleSheet.create({
  pillPressed: {
    opacity: 0.7,
  },
  avatarContainer: {
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    borderRadius: AVATAR_SIZE / 2,
    overflow: "hidden",
    marginRight: 8,
    flexShrink: 0,
  },
  avatarImage: {
    width: "100%",
    height: "100%",
  },
  textContainer: {
    flex: 1,
    justifyContent: "center",
  },
  textContainerCentered: {
    alignItems: "center",
  },
})
